import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createPaymentStages,
  createPaymentStagesBulk,
  deletePaymentStages,
  getAllBlocks,
  getPaymentStages,
  handleCheckPaymentStageDelete,
  updatePaymentStages,
  updateUnitPaymentStage
} from '../../services/api/implementation/impl';
import Modal from '../../../shared/components/ui/Modal';
import AddPaymentStage from '../forms/AddPaymentStage';
import EditPaymentStages from '../../pages/EditPaymentStages';
import AddBulkPaymentStage from '../forms/AddBulkPaymentStage';
import toast from 'react-hot-toast';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import Table from '../../../shared/components/layout/Table';
import { transformData } from '../../../super-admin/utils/transformData';
import { ChevronDown, Plus } from 'lucide-react';
import Button from '../../../shared/components/ui/Button';
import CommonPaymentModals from './CommonPaymentModals';
import { invalidateAccountsAfterStageChange } from '../../utils/invalidateAccountsAfterStageChange';

const GroupPaymentStages = ({ projectId, hideAddButton = false, showActions = true }) => {
  const queryClient = useQueryClient();
  const userId = Cookies.get('userId');
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [blockType, setBlockType] = useState('');
  const [blocksDynaWithLevels, setBlocksDynaWithLevels] = useState([]);
  const [isOpenPaymentStageModal, setIsOpenPaymentStagesModal] = useState(false);
  const [isOpenEditPaymentStageModal, setIsOpenEditPaymentStagesModal] = useState(false);
  const [paymentStagesId, setpaymentStagesId] = useState();
  const [Id, setId] = useState();
  const [isCompletedPayment, setIsCompletedPayment] = useState(false);
  const [paymentConfirmId, setpaymentConfirmId] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [isCurrentDay, setIsCurrentDay] = useState(true);
  const [isEditAllPayments, setIsEditAllPayments] = useState(false);
  const [incompletePayments, setIncompletePayments] = useState([]);
  const [isOpenBulkPaymentStageModal, setIsOpenBulkPaymentStageModal] = useState(false);
  const [canDelete, setCanDelete] = useState(null);
  const [resData, setResData] = useState();
  const [selectedIds, setSelectedIds] = useState([]);
  const [showChoiceModal, setShowChoiceModal] = useState(false);
  const [showUnitwiseModal, setShowUnitwiseModal] = useState(false);
  const [selectedPaymentStage, setSelectedPaymentStage] = useState(null);
  const [units, setUnits] = useState([]);
  const [isCompletingUnit, setIsCompletingUnit] = useState(false);
  const [isBlockDropdownOpen, setIsBlockDropdownOpen] = useState(false);

  // Fetch blocks for the project
  const {
    data: blocksDyna,
    refetch: refetchBlocks,
    error: blocksError
  } = useQuery({
    queryKey: ['blocks', projectId],
    queryFn: () => getAllBlocks(projectId),
    enabled: !!projectId,
    onError: (error) => {
      console.error('Error fetching blocks:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to fetch blocks');
    }
  });

  // Fetch payment stages for selected block
  const {
    data: AllPaymentStages,
    refetch: refetchpaymentstages,
    error: paymentStagesError
  } = useQuery({
    queryKey: ['payment_stages', selectedBlockId],
    queryFn: () => getPaymentStages(selectedBlockId),
    enabled: !!selectedBlockId,
    onError: (error) => {
      console.error('Error fetching payment stages:', error);
      toast.error(
        error?.response?.data?.message || error?.message || 'Failed to fetch payment stages'
      );
    }
  });

  // Track page view
  useEffect(() => {
    mixpanel.track('Group Payment Stages Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  // Set initial block selection
  useEffect(() => {
    if (blocksDyna) {
      const blocksWithLevels = blocksDyna.filter((block) => block?.levels?.length > 0);
      const sorted = blocksWithLevels.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setBlocksDynaWithLevels(sorted);

      if (sorted.length > 0 && !selectedBlockId) {
        setSelectedBlockId(sorted[0].id);
        setBlockType(sorted[0].type);
        console.log('Initial block selected:', sorted[0].id);
      }
      refetchpaymentstages();
    }
  }, [blocksDyna, selectedBlockId]);

  const PaymentFormat = [
    'id',
    'block',
    'name',
    'percentage',
    'status',
    'completion_date',
    'actions'
  ];

  const handlePaymentStagesFormat = (item, index) => {
    return {
      id: item.id,
      block: index === 0 ? item.block.name : null,
      name: item.name,
      percentage: item.percentage,
      status: item.is_completed == true ? 'Completed' : (showActions ? 'PaymentActions' : 'Pending'),
      completion_date: item.completion_date
        ? new Date(item.completion_date).toLocaleDateString()
        : '-'
    };
  };

  const paymentdata =
    AllPaymentStages?.map((item, index) =>
      transformData(handlePaymentStagesFormat(item, index), PaymentFormat)
    ) || [];

  const handleSelectBlockChange = (id) => {
    console.log('New Selected Block ID:', id);
    const block = blocksDynaWithLevels?.find((block) => block.id === id);
    if (block) {
      setBlockType(block.type);
      setSelectedBlockId(block.id);
    } else {
      setBlockType('');
      setSelectedBlockId(null);
    }
  };

  const handleCreateNewPaymentStages = () => {
    setIsOpenPaymentStagesModal(!isOpenPaymentStageModal);
  };

  const { mutate: handleOnPaymentStage } = useMutation({
    mutationFn: (payload) => createPaymentStages(selectedBlockId, payload),
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: () => {
      setIsSubmitting(false);
      toast.success('Payment Stages created Successfully');
      setIsOpenPaymentStagesModal(false);
      refetchpaymentstages();
    },
    onError: (error) => {
      setIsSubmitting(false);
      console.error('Error in payment stages:', error);
      toast.error(
        error?.response?.data?.message || error?.message || 'Error in Creating Payment Stages'
      );
    }
  });

  const handleEditPayment = (id) => {
    setIsOpenEditPaymentStagesModal(true);
    setId(id);
    const singleStage = AllPaymentStages.find((stage) => stage.id === parseInt(id));
    if (singleStage) {
      setpaymentStagesId(singleStage);
    }
  };

  const { mutate: handleOnEditPaymentStage } = useMutation({
    mutationFn: (payload) => updatePaymentStages(payload, payload.id),
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: () => {
      setIsSubmitting(false);
      toast.success('Payment Stage Updated Successfully');
      setIsOpenEditPaymentStagesModal(false);
      refetchpaymentstages();
    },
    onError: (error) => {
      setIsSubmitting(false);
      console.error('Error in updating payment stage:', error);
      toast.error(
        error?.response?.data?.message || error?.message || 'Error in Updating Payment Stage'
      );
    }
  });

  const { mutate: handleMarkupdate } = useMutation({
    mutationFn: (payload) => {
      const currentDate = new Date();
      const istOffset = 5.5 * 60 * 60 * 1000;
      const istDate = new Date(currentDate.getTime() + istOffset);
      const date = isCurrentDay ? istDate.toISOString().split('T')[0] : selectedDate;

      if (isCompletingUnit) {
        return updateUnitPaymentStage(
          {
            is_completed: true,
            completion_date: date
          },
          parseInt(paymentConfirmId)
        );
      } else {
        return updatePaymentStages(
          {
            is_completed: true,
            completion_date: date
          },
          parseInt(paymentConfirmId)
        );
      }
    },
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: (data) => {
      setIsSubmitting(false);
      toast.success('Payment is Completed');
      refetchpaymentstages();
      invalidateAccountsAfterStageChange(queryClient);
      setIsCompletedPayment(false);
      setSelectedDate('');
      setIsCurrentDay(true);

      const wasCompletingUnit = isCompletingUnit;
      setIsCompletingUnit(false);

      if (wasCompletingUnit && showUnitwiseModal) {
        setUnits((prevUnits) =>
          prevUnits.map((unit) =>
            unit.id === parseInt(paymentConfirmId)
              ? {
                ...unit,
                is_completed: true,
                completion_date: data?.completion_date || new Date().toISOString().split('T')[0]
              }
              : unit
          )
        );
      }
    },
    onError: (error) => {
      setIsSubmitting(false);
      setIsCompletingUnit(false);
      console.error('Error in updating payment stage:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Error in completed payment');
    }
  });

  const handleUnitwiseMarkComplete = (mappingId) => {
    setpaymentConfirmId(mappingId);
    setIsCompletingUnit(true);
    setIsCompletedPayment(true);
  };

  const handleConfimation = async (id) => {
    setpaymentConfirmId(id);
    setIsCompletingUnit(false);

    const paymentStage = AllPaymentStages?.find((stage) => stage.id === parseInt(id));

    if (paymentStage?.unitwise) {
      setUnits(paymentStage?.unitMappings || []);
      setSelectedPaymentStage(paymentStage);
      setShowUnitwiseModal(true);
    } else {
      setIsCompletedPayment(!isCompletedPayment);
    }
  };

  const { mutate: handleBulkPaymentUpdate } = useMutation({
    mutationFn: async ({ updatedStages, deletedStageIds }) => {
      const deletePromises = deletedStageIds.map((id) => deletePaymentStages(id));
      await Promise.all(deletePromises);

      const updatePromises = updatedStages.map((stage) =>
        updatePaymentStages(
          {
            name: stage.name,
            percentage: stage.percentage
          },
          stage.id
        )
      );

      await Promise.all(updatePromises);
    },
    onSuccess: () => {
      setIsSubmitting(false);
      toast.success('Payment stages updated successfully');
      setIsEditAllPayments(false);
      refetchpaymentstages();
    },
    onError: (error) => {
      setIsSubmitting(false);
      console.error('Error updating payment stages:', error);
      toast.error(
        error?.response?.data?.message || error?.message || 'Error updating payment stages'
      );
    }
  });

  const handleEditAllPayments = () => {
    const incomplete = AllPaymentStages?.filter((stage) => !stage.is_completed) || [];
    setIncompletePayments(incomplete);
    setIsEditAllPayments(true);
  };

  const { mutate: handleOnBulkPaymentStage } = useMutation({
    mutationFn: (payload) => createPaymentStagesBulk(null, payload),
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: () => {
      setIsSubmitting(false);
      toast.success('Bulk Payment Stages created Successfully');
      setIsOpenBulkPaymentStageModal(false);
      refetchpaymentstages();
    },
    onError: (error) => {
      setIsSubmitting(false);
      console.error('Error in bulk payment stages:', error);
      toast.error(
        error?.response?.data?.message || error?.message || 'Error in Creating Bulk Payment Stages'
      );
    }
  });

  const handleCreateBulkPaymentStages = () => {
    setIsOpenBulkPaymentStageModal(!isOpenBulkPaymentStageModal);
  };

  // Delete functions
  const { mutate: handleCheckDelete } = useMutation({
    mutationFn: (id) => handleCheckPaymentStageDelete(id),
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: async (res, id) => {
      setIsSubmitting(false);
      const payment = AllPaymentStages?.find((item) => parseInt(item.id) === parseInt(id));
      const paymentName = payment?.name || 'payment';
      if (res.canDelete) {
        setCanDelete('yes');
        setResData({ name: paymentName });
        setSelectedIds([parseInt(id)]);
      } else {
        setResData({ ...res, name: paymentName });
        setCanDelete('no');
      }
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error checking dependencies', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error checking dependencies');
    }
  });

  const { mutate: handleDeleteFn } = useMutation({
    mutationFn: (payload) => deletePaymentStages(payload),
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: () => {
      setIsSubmitting(false);
      toast.success('Payment Stage Deleted Successfully');
      refetchpaymentstages();
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error deleting payment stage', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error deleting payment stage');
    }
  });

  const HandleDelete = (data) => {
    const payload = { ids: data };
    handleDeleteFn(payload);
  };

  const transformedDataBlocks =
    blocksDynaWithLevels
      ?.filter((item) => item?.type === 'Group')
      ?.map((item) => ({
        id: item.id,
        value: item.id,
        label: item.name
      }))
      .sort((a, b) => a.label.localeCompare(b.label)) || [];

  const selectedBlockLabel =
    transformedDataBlocks.find((item) => item.id === selectedBlockId)?.label || 'Select Block';

  if (blocksError) {
    return <div>Error loading blocks: {blocksError.message}</div>;
  }

  return (
    <>
      {/* Modals */}
      {isOpenPaymentStageModal && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          name={`Payment Stages of '${transformedDataBlocks.find((item) => item.id === selectedBlockId)?.label}'`}
          width={55}
          onClose={() => setIsOpenPaymentStagesModal(false)}>
          <AddPaymentStage
            isSubmitting={isSubmitting}
            blockId={selectedBlockId}
            handleOnPaymentStage={handleOnPaymentStage}
            onClose={() => setIsOpenPaymentStagesModal(false)}
            blockType={blockType}
          />
        </Modal>
      )}

      {isOpenBulkPaymentStageModal && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          name={'Bulk Payment Stages'}
          width={55}
          onClose={() => setIsOpenBulkPaymentStageModal(false)}>
          <AddBulkPaymentStage
            isSubmitting={isSubmitting}
            projectId={projectId}
            handleOnPaymentStage={handleOnBulkPaymentStage}
            onClose={() => setIsOpenBulkPaymentStageModal(false)}
          />
        </Modal>
      )}

      {isOpenEditPaymentStageModal && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          name={'Edit Payment Stage'}
          width={55}
          onClose={() => setIsOpenEditPaymentStagesModal(false)}>
          <EditPaymentStages
            handleOnEditPaymentStage={handleOnEditPaymentStage}
            isSubmitting={isSubmitting}
            singleStageData={paymentStagesId}
            onClose={() => setIsOpenEditPaymentStagesModal(false)}
            blockType={blockType}
          />
        </Modal>
      )}

      {isEditAllPayments && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          name={'Edit All Payment Stages'}
          width={55}
          onClose={() => setIsEditAllPayments(false)}>
          <EditPaymentStages
            isSubmitting={isSubmitting}
            allPaymentStages={AllPaymentStages}
            handleOnEditPaymentStage={handleBulkPaymentUpdate}
            onClose={() => setIsEditAllPayments(false)}
            blockType={blockType}
          />
        </Modal>
      )}

      {/* Common Modals */}
      <CommonPaymentModals
        showChoiceModal={showChoiceModal}
        setShowChoiceModal={setShowChoiceModal}
        handleCreateNewPaymentStages={handleCreateNewPaymentStages}
        handleCreateBulkPaymentStages={handleCreateBulkPaymentStages}
        showUnitwiseModal={showUnitwiseModal}
        setShowUnitwiseModal={setShowUnitwiseModal}
        selectedPaymentStage={selectedPaymentStage}
        units={units}
        setUnits={setUnits}
        isSubmitting={isSubmitting}
        handleUnitwiseMarkComplete={handleUnitwiseMarkComplete}
        isCompletedPayment={isCompletedPayment}
        setIsCompletedPayment={setIsCompletedPayment}
        isCurrentDay={isCurrentDay}
        setIsCurrentDay={setIsCurrentDay}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        handleMarkupdate={handleMarkupdate}
        isCompletingUnit={isCompletingUnit}
        setIsCompletingUnit={setIsCompletingUnit}
        canDelete={canDelete}
        setCanDelete={setCanDelete}
        resData={resData}
        selectedIds={selectedIds}
        setSelectedIds={setSelectedIds}
        paymentdata={paymentdata}
        HandleDelete={HandleDelete}
      />

      {/* Main Content */}
      {paymentStagesError ? (
        <div className="flex justify-center h-[20rem]">No Data Found</div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <label htmlFor="group-block-filter" className="text-sm font-medium text-gray-700">
              Block Filter
            </label>
            <div className="relative min-w-[16rem]" id="group-block-filter">
              <button
                type="button"
                onClick={() => setIsBlockDropdownOpen((prev) => !prev)}
                className="flex h-10 w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 text-sm">
                <span className="truncate text-left">{selectedBlockLabel}</span>
                <ChevronDown
                  className={`h-4 w-4 text-gray-500 transition-transform ${
                    isBlockDropdownOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {isBlockDropdownOpen && (
                <>
                  <div className="absolute top-full left-0 z-30 mt-1 w-full max-h-56 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
                    {transformedDataBlocks.map((block) => (
                      <button
                        key={block.id}
                        type="button"
                        onClick={() => {
                          handleSelectBlockChange(block.id);
                          setIsBlockDropdownOpen(false);
                        }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                          block.id === selectedBlockId
                            ? 'bg-blue-50 text-blue-700 font-medium'
                            : 'text-gray-700'
                        }`}>
                        {block.label}
                      </button>
                    ))}
                  </div>
                  <div
                    className="fixed inset-0 z-20"
                    onClick={() => setIsBlockDropdownOpen(false)}
                    role="button"
                    tabIndex={-1}
                  />
                </>
              )}
            </div>
          </div>
          <div className="table-container h-[calc(100vh-10rem)] overflow-auto">
            <style>{`
            .table-container .table-body {
              position: static !important;
              overflow-y: visible !important;
              max-height: none !important;
              height: auto !important;
              top: auto !important;
              padding-bottom: 0 !important;
            }
            .table-container .table-container {
              height: auto !important;
            }
            .table-container > div {
              height: auto !important;
            }

          `}</style>
            <Table
              headers={[
                'Block',
                'Payment Stages',
                'Payment Percentage',
                'Status',
                'Date',
                ...(showActions ? ['Action'] : [])
              ]}
              data={paymentdata}
              isMarkEnable={showActions}
              identifier={'id'}
              onMark={(id) => handleConfimation(id)}
              onEdit={(id) => handleEditPayment(id)}
              isEditEnable={showActions}
              isDeleteEnable={showActions}
              onDelete={handleCheckDelete}
              isWrap={true}
            />
          </div>
        </div>
      )}

      {/* Floating Add Button */}
      {!hideAddButton && (
        <div className="fixed bottom-8 right-8 z-50">
          <Button
            displayType="primary"
            onClick={() => {
              mixpanel.track('Add Payment Stages Button Clicked', {
                user_id: userId,
                timestamp: new Date().toISOString()
              });
              setShowChoiceModal(true);
            }}
            customStyle={{
              borderRadius: '50%',
              width: '50px',
              height: '50px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
            }}>
            <Plus size={24} color="white" />
          </Button>
        </div>
      )}
    </>
  );
};

export default GroupPaymentStages;
